import React, { useContext } from "react"
import { IntlConfig, IntlProvider, useIntl } from "react-intl"
import { Helmet } from "react-helmet"
import { AdapterDayjs, dayjs, LocalizationProvider } from "@carrotfertility/carotene-core"
import { setHeapLocale } from "#/utils/heap"
import { getLastUsedLocale, setLastUsedLocale } from "../../../utils/lastUsedLocale"
import { setOneTrustLocale } from "#/utils/oneTrustLocale"
import { DEFAULT_LOCALE, Locale, SupportedLocale } from "../../../types/Locale"
import { reportError } from "#/utils/ErrorReporting"
import useGetMemberCountry from "#/components/company/hooks/useGetMemberCountry"

type CompiledMessages = IntlConfig["messages"]

type LocaleProps = {
  setLocale: (locale: SupportedLocale) => void
  didCompiledMessagesLoad: boolean
}

const LocaleContext = React.createContext<LocaleProps>({
  setLocale: null,
  didCompiledMessagesLoad: false
})

async function loadLocaleMessages(locale: Locale): Promise<CompiledMessages> {
  try {
    const messages = await import(`compiledLang/${locale.toString()}.json`)
    return messages.default
  } catch (error) {
    reportError(
      new Error(`Could not load messages for locale\nLocale: [${locale?.toString()}] \nCause:${error.message}`, {
        cause: error.cause
      })
    )
    /* Ignored so that an error doesn't prevent app from loading */
  }
}

function getDayJsLanguageTag(locale: Locale, countryCode?: string): string {
  // ensure US date formatting is used for the en-US locale when the country is unknown, such as when unauthenticated
  if (!countryCode && locale === SupportedLocale.English_UnitedStates) {
    return SupportedLocale.English_UnitedStates.dayJs.countryLanguageTagOverrides.US
  }

  return countryCode && locale.dayJs.countryLanguageTagOverrides?.[countryCode]
    ? locale.dayJs.countryLanguageTagOverrides[countryCode]
    : locale.dayJs.languageTag
}

async function loadIntlPolyfills(locale: Locale): Promise<void> {
  if (locale instanceof SupportedLocale) {
    try {
      await import(`intlPolyfills/${locale.toString()}/index.ts`)
    } catch (error) {
      reportError(
        new Error(
          `Could not load Intl polyfills for locale\nLocale: [${locale?.toString()}] \nCause:${error.message}`,
          {
            cause: error.cause
          }
        )
      )
      /* Ignored so that an error doesn't prevent app from loading */
    }
  }
}

async function loadDayJsPolyfills(dayJsLanguageTag: string): Promise<void> {
  try {
    await import(`dayJsPolyfills/${dayJsLanguageTag}/index.ts`)
  } catch (error) {
    reportError(
      new Error(
        `Could not load DayJS polyfills for language tag\nLanguage Tag: [${dayJsLanguageTag}] \nCause:${error.message}`,
        {
          cause: error.cause
        }
      )
    )
    /* Ignored so that an error doesn't prevent app from loading */
  }
}

function loadFont(locale: Locale): void {
  const fontUrls: { [key: string]: string } = {
    [SupportedLocale.Thai_Thailand.toString()]:
      "https://fonts.googleapis.com/css2?family=Noto+Sans+Thai+Looped:wght@100;200;300;400;500;600;700;800;900&display=swap",
    [SupportedLocale.SimplifiedChinese_China.toString()]:
      "https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@100..900&display=swap",
    [SupportedLocale.TraditionalChinese_Taiwan.toString()]:
      "https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@100..900&display=swap",
    [SupportedLocale.Japanese_Japan.toString()]:
      "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@100..900&display=swap",
    [SupportedLocale.Urdu_Pakistan.toString()]:
      "https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@100..900&display=swap",
    [SupportedLocale.Georgian_Georgia.toString()]:
      "https://fonts.googleapis.com/css2?family=Noto+Serif+Georgian:wght@100..900&display=swap"
  }

  const fontUrl = fontUrls[locale.toString()]
  if (fontUrl) {
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = fontUrl
    document.head.appendChild(link)
  }
}

export const useLocaleContext = () => useContext(LocaleContext)

type ActiveLocale = { locale: SupportedLocale; messages: CompiledMessages; dayJsLocale: string }

export function IntlProviderWrapper(props: React.PropsWithChildren) {
  // selectedLocale is the target; active is what's applied to the tree. They swap together only after the target's
  // messages load, so a switch never briefly renders the new locale against the old messages (a flash of English).
  const [selectedLocale, setSelectedLocale] = React.useState<SupportedLocale>(
    () => getLastUsedLocale() ?? DEFAULT_LOCALE
  )
  const [active, setActive] = React.useState<ActiveLocale>()
  const hasActivated = React.useRef(false)

  const memberUserCountry = useGetMemberCountry()
  const countryCode = memberUserCountry?.data?.countryCode

  React.useEffect(() => {
    let cancelled = false
    const dayJsLocale = getDayJsLanguageTag(selectedLocale, countryCode)

    // Persist eagerly (before the async load) so the choice survives even if the load is interrupted.
    setOneTrustLocale(selectedLocale)
    setLastUsedLocale(selectedLocale)
    setHeapLocale(selectedLocale)

    async function loadActiveLocale(): Promise<void> {
      const [messages] = await Promise.all([
        loadLocaleMessages(selectedLocale),
        loadIntlPolyfills(selectedLocale),
        loadDayJsPolyfills(dayJsLocale)
      ])
      if (cancelled) return

      const loadFailed = !messages
      const isFirstLoad = !hasActivated.current
      // A failed switch keeps the working locale; the first load commits anyway so the app still renders (in English)
      // instead of staying stuck on the loading screen.
      if (loadFailed && !isFirstLoad) return

      hasActivated.current = true
      loadFont(selectedLocale)
      setActive({ locale: selectedLocale, messages, dayJsLocale })
    }

    loadActiveLocale()

    return () => {
      cancelled = true
    }
  }, [selectedLocale, countryCode])

  const intlLocale = active?.locale ?? DEFAULT_LOCALE
  const dayJsLocale = active?.dayJsLocale ?? getDayJsLanguageTag(DEFAULT_LOCALE, countryCode)

  return (
    <LocaleContext.Provider value={{ setLocale: setSelectedLocale, didCompiledMessagesLoad: active != null }}>
      <IntlProvider
        locale={intlLocale.toString()}
        defaultLocale={DEFAULT_LOCALE.toString()}
        messages={active?.messages}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale(dayJsLocale)}>
          <DocumentLocalizationAttributes />
          {props.children}
        </LocalizationProvider>
      </IntlProvider>
    </LocaleContext.Provider>
  )
}

function DocumentLocalizationAttributes() {
  const { locale } = useIntl()
  const { textDirection } = Locale.getLocaleFromString(locale)

  return (
    <Helmet>
      <html lang={locale} dir={textDirection} />
    </Helmet>
  )
}
